/**
 * Resposta do Assistente pronta para copiar e colar em outro lugar.
 *
 * Vai à área de transferência em dois formatos, a partir da mesma árvore do
 * `Markdown.jsx` (`lerMarkdown`):
 * - texto simples, sem as marcas do Markdown (nada de "**" ou "###" colado
 *   no WhatsApp ou no e-mail): título numa linha, item com "•";
 * - HTML com negrito e listas, para Word, Gmail e Docs manterem a
 *   formatação (título vira parágrafo em negrito).
 *
 * O texto simples também é o trecho da citação ("Respondendo ao assistente").
 */
import { lerMarkdown } from "./markdownEmBlocos";

const MARCADOR = "•";
const MARCADOR_DO_SUBITEM = "◦";
const RECUO_DO_SUBITEM = "   ";

function textoDosTrechos(trechos) {
  return (trechos || []).map((t) => (t.filhos ? textoDosTrechos(t.filhos) : t.texto)).join("");
}

function linhasDaLista(lista, recuo = "") {
  const marcador = recuo ? MARCADOR_DO_SUBITEM : MARCADOR;
  return lista.itens.flatMap((item, i) => {
    const rotulo = lista.ordenada ? `${lista.inicio + i}.` : marcador;
    const [primeira, ...continuacao] = item.trechos.map(textoDosTrechos);
    const linhas = [`${recuo}${rotulo} ${primeira}`, ...continuacao.map((l) => `${recuo}  ${l}`)];
    return item.sublista ? [...linhas, ...linhasDaLista(item.sublista, recuo + RECUO_DO_SUBITEM)] : linhas;
  });
}

function textoDoBloco(bloco) {
  if (bloco.tipo === "titulo") return textoDosTrechos(bloco.trechos);
  if (bloco.tipo === "lista") return linhasDaLista(bloco).join("\n");
  return bloco.linhas.map(textoDosTrechos).join("\n");
}

/** Texto simples, sem marcas: blocos separados por uma linha em branco. */
export function textoParaCopiar(markdown) {
  return lerMarkdown(markdown).map(textoDoBloco).join("\n\n");
}

function escapar(texto) {
  return String(texto)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const TAG_DO_TRECHO = Object.freeze({ negrito: "strong", italico: "em", codigo: "code" });

function htmlDosTrechos(trechos) {
  return (trechos || [])
    .map((t) => {
      const tag = TAG_DO_TRECHO[t.tipo];
      const conteudo = t.filhos ? htmlDosTrechos(t.filhos) : escapar(t.texto);
      return tag ? `<${tag}>${conteudo}</${tag}>` : conteudo;
    })
    .join("");
}

function htmlDaLista(lista) {
  const itens = lista.itens
    .map((item) => {
      const corpo = item.trechos.map(htmlDosTrechos).join("<br>");
      return `<li>${corpo}${item.sublista ? htmlDaLista(item.sublista) : ""}</li>`;
    })
    .join("");
  if (!lista.ordenada) return `<ul>${itens}</ul>`;
  return lista.inicio === 1 ? `<ol>${itens}</ol>` : `<ol start="${lista.inicio}">${itens}</ol>`;
}

function htmlDoBloco(bloco) {
  // Título como parágrafo em negrito: <h5> colado no Gmail sai menor que o texto.
  if (bloco.tipo === "titulo") return `<p><strong>${htmlDosTrechos(bloco.trechos)}</strong></p>`;
  if (bloco.tipo === "lista") return htmlDaLista(bloco);
  return `<p>${bloco.linhas.map(htmlDosTrechos).join("<br>")}</p>`;
}

/** HTML formatado (títulos, listas, negrito); todo texto da IA sai escapado. */
export function htmlParaCopiar(markdown) {
  return lerMarkdown(markdown).map(htmlDoBloco).join("");
}

/**
 * Copia a resposta nos dois formatos. Sem `ClipboardItem` (navegador antigo
 * ou recusa), cai no texto simples. Devolve se conseguiu.
 */
export async function copiarResposta(markdown) {
  const texto = textoParaCopiar(markdown);
  try {
    if (typeof window.ClipboardItem === "function" && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/plain": new Blob([texto], { type: "text/plain" }),
          "text/html": new Blob([htmlParaCopiar(markdown)], { type: "text/html" }),
        }),
      ]);
      return true;
    }
  } catch {
    // Alguns navegadores recusam o HTML: tenta o texto simples abaixo.
  }
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}
